// HeroSelector.jsx - Grid-based Hero Selection with Categories
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Users, Search, X, ChevronDown, Sword, Shield, Heart, Zap, Skull, Crosshair, Check, LayoutGrid, List, Layers } from "lucide-react";

// ===================== Constants =====================
const ROLES = {
  Fighter: { gradient: "linear-gradient(135deg, #f59e0b, #ea580c)", color: "#f59e0b", icon: Sword, bg: "#f59e0b20" },
  Mage: { gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)", color: "#8b5cf6", icon: Zap, bg: "#8b5cf620" },
  Tank: { gradient: "linear-gradient(135deg, #64748b, #475569)", color: "#64748b", icon: Shield, bg: "#64748b20" },
  Support: { gradient: "linear-gradient(135deg, #10b981, #059669)", color: "#10b981", icon: Heart, bg: "#10b98120" },
  Assassin: { gradient: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#ef4444", icon: Skull, bg: "#ef444420" },
  Assasin: { gradient: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#ef4444", icon: Skull, bg: "#ef444420" },
  Carry: { gradient: "linear-gradient(135deg, #0ea5e9, #0284c7)", color: "#0ea5e9", icon: Crosshair, bg: "#0ea5e920" },
  Marksman: { gradient: "linear-gradient(135deg, #0ea5e9, #0284c7)", color: "#0ea5e9", icon: Crosshair, bg: "#0ea5e920" },
  Unknown: { gradient: "linear-gradient(135deg, #6b7280, #4b5563)", color: "#6b7280", icon: Users, bg: "#6b728020" },
};

const LANES_DATA = {
  DSL: { name: "Dark Slayer Lane", emoji: "⚔️", color: "#f59e0b" },
  MID: { name: "Mid Lane", emoji: "🔮", color: "#8b5cf6" },
  SUP: { name: "Support", emoji: "💚", color: "#10b981" },
  JUG: { name: "Jungle", emoji: "🌲", color: "#22c55e" },
  ADL: { name: "Abyssal Dragon Lane", emoji: "🎯", color: "#0ea5e9" },
  Unknown: { name: "Unknown", emoji: "❓", color: "#6b7280" },
};

const GROUP_BY_OPTIONS = [
  { value: "none", label: "No Grouping", icon: LayoutGrid },
  { value: "role", label: "Group by Role", icon: Layers },
  { value: "lane", label: "Group by Lane", icon: List },
];

// ===================== Styles =====================
const styles = `
  @keyframes modalFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes modalSlideUp {
    from { opacity: 0; transform: scale(0.9) translateY(20px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes cardPop {
    0% { transform: scale(1); }
    50% { transform: scale(0.92); }
    100% { transform: scale(1); }
  }
  @keyframes selectedGlow {
    0%, 100% { box-shadow: 0 0 15px rgba(255,16,38,0.5); }
    50% { box-shadow: 0 0 25px rgba(255,16,38,0.8); }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes categorySlide {
    from { opacity: 0; transform: translateX(-10px); }
    to { opacity: 1; transform: translateX(0); }
  }
  .hero-modal-overlay {
    animation: modalFadeIn 0.2s ease-out;
  }
  .hero-modal-content {
    animation: modalSlideUp 0.3s ease-out;
  }
  .hero-grid-card {
    transition: all 0.2s ease;
  }
  .hero-grid-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 10px 30px rgba(255,16,38,0.3);
  }
  .hero-grid-card:active {
    animation: cardPop 0.15s ease-out;
  }
  .hero-grid-card.selected {
    animation: selectedGlow 1.5s ease-in-out infinite;
  }
  .hero-image-shimmer {
    background: linear-gradient(90deg, rgba(255,16,38,0.05) 25%, rgba(255,16,38,0.15) 50%, rgba(255,16,38,0.05) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }
  .role-tab {
    transition: all 0.2s ease;
  }
  .role-tab:hover {
    transform: translateY(-2px);
  }
  .role-tab.active {
    transform: translateY(-2px);
  }
  .category-section {
    animation: categorySlide 0.3s ease-out;
  }
  .scroll-container::-webkit-scrollbar { width: 6px; height: 6px; }
  .scroll-container::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.3); border-radius: 10px; }
  .scroll-container::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, #ff1026, #a30013);
    border-radius: 10px;
  }
  .group-option {
    transition: all 0.2s ease;
  }
  .group-option:hover {
    background: rgba(255,16,38,0.15);
  }
  .group-option.active {
    background: rgba(255,16,38,0.2);
    border-color: #ff1026;
  }
`;

// ===================== Sub Components =====================

// Hero Avatar
const HeroAvatar = ({ heroName, imageUrl, size = 64 }) => {
  const [imgError, setImgError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setImgError(false);
    setLoaded(false);
  }, [imageUrl]);

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "12px",
        overflow: "hidden",
        background: "linear-gradient(135deg, #1a1a1a, #2a2a2a)",
        position: "relative",
      }}
    >
      {(!loaded || !imageUrl) && !imgError && (
        <div
          className="hero-image-shimmer"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Users size={size * 0.4} color="#ff1026" style={{ opacity: 0.4 }} />
        </div>
      )}

      {imageUrl && !imgError ? (
        <img
          src={imageUrl}
          alt={heroName}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
          onLoad={() => setLoaded(true)}
          onError={() => setImgError(true)}
        />
      ) : imgError ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Users size={size * 0.4} color="#ff1026" />
        </div>
      ) : null}
    </div>
  );
};

// Hero Grid Card
const HeroGridCard = ({ hero, imageUrl, isSelected, onClick }) => {
  const roleData = ROLES[hero.role] || ROLES.Unknown;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`hero-grid-card ${isSelected ? "selected" : ""}`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "8px 6px",
        background: isSelected
          ? "linear-gradient(135deg, rgba(255,16,38,0.25), rgba(255,16,38,0.1))"
          : "rgba(20, 20, 20, 0.8)",
        border: isSelected ? "2px solid #ff1026" : "1px solid rgba(255,16,38,0.15)",
        borderRadius: "12px",
        cursor: "pointer",
        color: "#fff",
        fontFamily: "'Rajdhani', sans-serif",
        position: "relative",
        minWidth: 0,
      }}
    >
      {/* Selected Check */}
      {isSelected && (
        <div
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            background: "#ff1026",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 8px rgba(255,16,38,0.8)",
          }}
        >
          <Check size={10} color="#fff" strokeWidth={3} />
        </div>
      )}

      {/* Hero Image */}
      <div
        style={{
          width: "52px",
          height: "52px",
          borderRadius: "10px",
          overflow: "hidden",
          border: isSelected ? "2px solid #ff1026" : "1px solid rgba(255,16,38,0.3)",
          marginBottom: "6px",
          boxShadow: isSelected ? "0 0 12px rgba(255,16,38,0.5)" : "none",
        }}
      >
        <HeroAvatar heroName={hero.name} imageUrl={imageUrl} size={52} />
      </div>

      {/* Hero Name */}
      <div
        style={{
          fontWeight: "bold",
          fontSize: "11px",
          textAlign: "center",
          color: isSelected ? "#ff1026" : "#fff",
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: 1.2,
        }}
      >
        {hero.name}
      </div>
    </button>
  );
};

// Category Section Header
const CategoryHeader = ({ title, count, icon: Icon, color, emoji }) => (
  <div
    className="category-section"
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 12px",
      marginBottom: "10px",
      background: `linear-gradient(90deg, ${color}20, transparent)`,
      borderLeft: `3px solid ${color}`,
      borderRadius: "0 10px 10px 0",
    }}
  >
    {emoji && <span style={{ fontSize: "16px" }}>{emoji}</span>}
    {Icon && !emoji && <Icon size={16} color={color} />}
    <span style={{ fontWeight: "bold", fontSize: "13px", color: "#fff" }}>{title}</span>
    <span
      style={{
        padding: "2px 8px",
        borderRadius: "10px",
        background: `${color}30`,
        fontSize: "11px",
        color: color,
        fontWeight: "bold",
      }}
    >
      {count}
    </span>
  </div>
);

// Role Tab Button
const RoleTab = ({ role, isActive, onClick, count, roleData }) => {
  const Icon = roleData?.icon || Users;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`role-tab ${isActive ? "active" : ""}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        padding: "6px 10px",
        borderRadius: "8px",
        border: isActive ? `2px solid ${roleData?.color || "#ff1026"}` : "1px solid rgba(255, 255, 255, 0.1)",
        background: isActive
          ? `linear-gradient(135deg, ${roleData?.color || "#ff1026"}30, ${roleData?.color || "#ff1026"}10)`
          : "rgba(0, 0, 0, 0.4)",
        color: isActive ? roleData?.color || "#ff1026" : "#9ca3af",
        cursor: "pointer",
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: "bold",
        fontSize: "11px",
        whiteSpace: "nowrap",
        boxShadow: isActive ? `0 2px 10px ${roleData?.color || "#ff1026"}40` : "none",
      }}
    >
      <Icon size={12} />
      <span>{role === "ALL" ? "All" : role}</span>
      <span
        style={{
          padding: "1px 5px",
          borderRadius: "6px",
          background: isActive ? `${roleData?.color || "#ff1026"}40` : "rgba(255, 255, 255, 0.1)",
          fontSize: "10px",
        }}
      >
        {count}
      </span>
    </button>
  );
};

// Group By Toggle
const GroupByToggle = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: "4px" }}>
    {GROUP_BY_OPTIONS.map((opt) => {
      const Icon = opt.icon;
      const isActive = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`group-option ${isActive ? "active" : ""}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "6px 10px",
            borderRadius: "8px",
            border: isActive ? "1px solid #ff1026" : "1px solid rgba(255,16,38,0.2)",
            background: isActive ? "rgba(255,16,38,0.2)" : "rgba(0, 0, 0, 0.4)",
            color: isActive ? "#ff1026" : "#9ca3af",
            cursor: "pointer",
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: "bold",
            fontSize: "11px",
            whiteSpace: "nowrap",
          }}
          title={opt.label}
        >
          <Icon size={12} />
          {isActive && <span>{opt.label.replace("Group by ", "")}</span>}
        </button>
      );
    })}
  </div>
);

// ===================== Main Component =====================
const HeroSelector = ({
  heroes,
  value,
  onChange,
  label,
  placeholder = "Select Hero",
  getHeroImageUrl,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [groupBy, setGroupBy] = useState("role"); // "none" | "role" | "lane"
  const searchInputRef = useRef(null);

  // Get selected hero
  const selectedHero = useMemo(() => heroes.find((h) => h.name === value) || null, [heroes, value]);

  // Get unique roles
  const roles = useMemo(() => {
    const roleSet = new Set(heroes.map((h) => h.role || "Unknown"));
    return ["ALL", ...Array.from(roleSet).sort()];
  }, [heroes]);

  // Role counts
  const roleCounts = useMemo(() => {
    const counts = { ALL: heroes.length };
    heroes.forEach((h) => {
      const role = h.role || "Unknown";
      counts[role] = (counts[role] || 0) + 1;
    });
    return counts;
  }, [heroes]);

  // Filter heroes
  const filteredHeroes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return heroes
      .filter((h) => (roleFilter === "ALL" ? true : (h.role || "Unknown") === roleFilter))
      .filter((h) => (!query ? true : h.name.toLowerCase().includes(query)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [heroes, searchQuery, roleFilter]);

  // Group heroes
  const groupedHeroes = useMemo(() => {
    if (groupBy === "none") {
      return [{ key: "all", heroes: filteredHeroes }];
    }

    const groups = new Map();

    filteredHeroes.forEach((hero) => {
      const key = groupBy === "role" ? (hero.role || "Unknown") : (hero.lane || "Unknown");
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(hero);
    });

    // Sort groups
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
      if (a === "Unknown") return 1;
      if (b === "Unknown") return -1;
      return a.localeCompare(b);
    });

    return sortedKeys.map((key) => ({
      key,
      heroes: groups.get(key),
    }));
  }, [filteredHeroes, groupBy]);

  // Focus search when opened
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // Prevent body scroll when modal open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleSelect = (heroName) => {
    onChange({ target: { value: heroName } });
    setOpen(false);
    setSearchQuery("");
  };

  const selectedImageUrl = selectedHero && getHeroImageUrl ? getHeroImageUrl(selectedHero.name) : null;
  const selectedRoleData = selectedHero ? ROLES[selectedHero.role] || ROLES.Unknown : null;

  // Get category info
  const getCategoryInfo = (key) => {
    if (groupBy === "role") {
      const roleData = ROLES[key] || ROLES.Unknown;
      return {
        title: key,
        icon: roleData.icon,
        color: roleData.color,
        emoji: null,
      };
    } else if (groupBy === "lane") {
      const laneData = LANES_DATA[key] || LANES_DATA.Unknown;
      return {
        title: laneData.name,
        icon: null,
        color: laneData.color,
        emoji: laneData.emoji,
      };
    }
    return { title: "All Heroes", icon: Users, color: "#ff1026", emoji: null };
  };

  return (
    <>
      <style>{styles}</style>

      <div style={{ minWidth: 0, flex: 1, position: "relative" }}>
        {/* Label */}
        {label && (
          <div
            style={{
              color: "#9ca3af",
              fontWeight: "bold",
              marginBottom: "6px",
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {label}
          </div>
        )}

        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "8px 12px",
            background: selectedHero
              ? "linear-gradient(135deg, rgba(255,16,38,0.15), rgba(0, 0, 0, 0.8))"
              : "rgba(0, 0, 0, 0.6)",
            border: selectedHero ? "2px solid rgba(255,16,38,0.5)" : "2px solid rgba(255,16,38,0.25)",
            borderRadius: "12px",
            color: "#ffffff",
            cursor: "pointer",
            transition: "all 0.3s ease",
            fontFamily: "'Rajdhani', sans-serif",
            outline: "none",
            boxSizing: "border-box",
            minWidth: 0,
          }}
        >
          {/* Hero Image */}
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              overflow: "hidden",
              border: selectedHero ? "2px solid rgba(255,16,38,0.6)" : "2px solid rgba(255,16,38,0.2)",
              background: "rgba(0, 0, 0, 0.5)",
              flexShrink: 0,
              boxShadow: selectedHero ? "0 0 12px rgba(255,16,38,0.3)" : "none",
            }}
          >
            {selectedImageUrl ? (
              <img
                src={selectedImageUrl}
                alt={selectedHero?.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Users size={22} color="#ff1026" />
              </div>
            )}
          </div>

          {/* Hero Info */}
          <div style={{ flex: 1, textAlign: "left", minWidth: 0, overflow: "hidden" }}>
            {selectedHero ? (
              <>
                <div 
                  style={{ 
                    fontWeight: "bold", 
                    fontSize: "15px", 
                    color: "#fff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    marginBottom: "2px",
                  }}
                >
                  {selectedHero.name}
                </div>
                <div 
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "6px", 
                    fontSize: "12px",
                  }}
                >
                  {selectedRoleData && React.createElement(selectedRoleData.icon, { size: 12, color: selectedRoleData.color })}
                  <span style={{ color: selectedRoleData?.color || "#9ca3af", fontWeight: "bold" }}>
                    {selectedHero.role}
                  </span>
                  <span style={{ color: "#4b5563" }}>•</span>
                  <span style={{ color: "#9ca3af" }}>
                    {LANES_DATA[selectedHero.lane]?.emoji || ""} {selectedHero.lane}
                  </span>
                </div>
              </>
            ) : (
              <span style={{ color: "#6b7280", fontSize: "14px" }}>{placeholder}</span>
            )}
          </div>

          <ChevronDown size={18} color="#ff1026" style={{ flexShrink: 0 }} />
        </button>
      </div>

      {/* Modal Overlay */}
      {open && (
        <div
          className="hero-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.9)",
            backdropFilter: "blur(10px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            boxSizing: "border-box",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          {/* Modal Content */}
          <div
            className="hero-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "calc(100vw - 32px)",
              maxWidth: "880px",
              height: "calc(100vh - 32px)",
              maxHeight: "680px",
              background: "linear-gradient(180deg, rgba(20, 20, 20, 0.99), rgba(10, 10, 10, 0.99))",
              border: "2px solid rgba(255,16,38,0.4)",
              borderRadius: "20px",
              boxShadow: "0 0 80px rgba(255,16,38,0.2), 0 25px 50px rgba(0, 0, 0, 0.8)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "14px 20px",
                borderBottom: "1px solid rgba(255,16,38,0.25)",
                background: "rgba(0, 0, 0, 0.5)",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                <div>
                  <h2
                    style={{
                      fontFamily: "'Orbitron', sans-serif",
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#fff",
                      margin: 0,
                    }}
                  >
                    SELECT HERO
                  </h2>
                  <p style={{ color: "#6b7280", fontSize: "11px", margin: "2px 0 0 0" }}>
                    {filteredHeroes.length} heroes available
                  </p>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {/* Group By Toggle */}
                  <GroupByToggle value={groupBy} onChange={setGroupBy} />

                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "10px",
                      border: "2px solid rgba(255,16,38,0.3)",
                      background: "rgba(0, 0, 0, 0.5)",
                      color: "#ff1026",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Search */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 12px",
                  background: "rgba(0, 0, 0, 0.5)",
                  border: "2px solid rgba(255,16,38,0.25)",
                  borderRadius: "10px",
                }}
              >
                <Search size={16} color="#ff1026" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search hero name..."
                  style={{
                    flex: 1,
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "#fff",
                    fontSize: "13px",
                    fontFamily: "'Rajdhani', sans-serif",
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      searchInputRef.current?.focus();
                    }}
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "6px",
                      border: "none",
                      background: "rgba(255,16,38,0.2)",
                      color: "#ff1026",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>

            {/* Role Tabs */}
            <div
  className="scroll-container"
  onWheel={(e) => e.stopPropagation()}
  onTouchMove={(e) => e.stopPropagation()}
  style={{
    padding: "10px 20px",
    borderBottom: "1px solid rgba(255,16,38,0.15)",
    overflowX: "auto",
    background: "rgba(0, 0, 0, 0.3)",
    flexShrink: 0,

    overscrollBehaviorX: "contain",
    WebkitOverflowScrolling: "touch",
  }}
>

              <div style={{ display: "flex", gap: "6px" }}>
                {roles.map((role) => (
                  <RoleTab
                    key={role}
                    role={role}
                    isActive={roleFilter === role}
                    onClick={() => setRoleFilter(role)}
                    count={roleCounts[role]}
                    roleData={role === "ALL" ? { color: "#ff1026", icon: Users } : ROLES[role] || ROLES.Unknown}
                  />
                ))}
              </div>
            </div>

            {/* Hero Grid with Categories */}
<div
  className="scroll-container"
  onWheel={(e) => e.stopPropagation()}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => e.stopPropagation()}
  onTouchMove={(e) => e.stopPropagation()}
  style={{
    flex: 1,
    padding: "14px 20px",
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,

    // ✅ สำคัญ: กัน scroll chaining / เด้งกลับ
    overscrollBehavior: "contain",

    // ✅ มือถือ/iOS ให้เลื่อนลื่นและไม่เด้ง
    WebkitOverflowScrolling: "touch",

    // ✅ ช่วยให้ gesture เลื่อนแกน Y ชัดเจน
    touchAction: "pan-y",
  }}
>

              {filteredHeroes.length > 0 ? (
                groupedHeroes.map((group) => {
                  const catInfo = getCategoryInfo(group.key);
                  return (
                    <div key={group.key} style={{ marginBottom: "16px" }}>
                      {/* Category Header */}
                      {groupBy !== "none" && (
                        <CategoryHeader
                          title={catInfo.title}
                          count={group.heroes.length}
                          icon={catInfo.icon}
                          color={catInfo.color}
                          emoji={catInfo.emoji}
                        />
                      )}

                      {/* Heroes Grid */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(85px, 1fr))",
                          gap: "8px",
                        }}
                      >
                        {group.heroes.slice(0, 150).map((hero) => (
                          <HeroGridCard
                            key={hero.name}
                            hero={hero}
                            imageUrl={getHeroImageUrl ? getHeroImageUrl(hero.name) : null}
                            isSelected={hero.name === value}
                            onClick={() => handleSelect(hero.name)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "40px 20px",
                    color: "#6b7280",
                  }}
                >
                  <Users size={50} style={{ marginBottom: "12px", opacity: 0.3 }} />
                  <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "6px" }}>No heroes found</div>
                  <div style={{ fontSize: "13px" }}>Try a different search or filter</div>
                </div>
              )}
            </div>

            {/* Footer - Selected Hero Preview */}
            {selectedHero && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid rgba(255,16,38,0.25)",
                  background: "linear-gradient(180deg, rgba(255,16,38,0.1), rgba(0, 0, 0, 0.6))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: "2px solid #ff1026",
                      boxShadow: "0 0 12px rgba(255,16,38,0.5)",
                    }}
                  >
                    {selectedImageUrl && (
                      <img
                        src={selectedImageUrl}
                        alt={selectedHero.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    )}
                  </div>
                  <div>
                    <div style={{ color: "#9ca3af", fontSize: "10px" }}>Selected</div>
                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#ff1026" }}>{selectedHero.name}</div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "3px 8px",
                      borderRadius: "6px",
                      background: selectedRoleData?.bg,
                      fontSize: "10px",
                      color: selectedRoleData?.color,
                      fontWeight: "bold",
                    }}
                  >
                    {selectedRoleData && React.createElement(selectedRoleData.icon, { size: 10 })}
                    <span>{selectedHero.role}</span>
                  </div>
                  <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                    {LANES_DATA[selectedHero.lane]?.emoji} {LANES_DATA[selectedHero.lane]?.name || selectedHero.lane}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{
                    padding: "8px 20px",
                    borderRadius: "8px",
                    border: "none",
                    background: "linear-gradient(135deg, #ff1026, #a30013)",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "12px",
                    cursor: "pointer",
                    fontFamily: "'Rajdhani', sans-serif",
                    boxShadow: "0 4px 15px rgba(255,16,38,0.4)",
                    transition: "all 0.2s ease",
                  }}
                >
                  CONFIRM
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSelector;